/**
 * ShopAll Product - Subscription toggle functionality
 * Handles monthly/one-time purchase option switching for product forms
 */

document.addEventListener("DOMContentLoaded", function () {
  // Configuration object for maintainability
  const CONFIG = {
    selectors: {
      productForm: ".product-form",
      productTypeInput: 'input[name^="product-type"]',
      shopallCta: ".shopall-cta",
      sellingPlanInput: (handle) => `#${handle}-selling_plan`,
      monthlyLabel: (handle) => `label[for="${handle}-monthly"]`,
      oneTimeLabel: (handle) => `label[for="${handle}-one_time"]`,
      variantSwitcherContainer: ".ShopAll-variantSwitcher-container",
      productContainer: ".shopall-product-container"
    },
    classes: {
      active: "shopall-cta-active",
      inactive: "shopall-cta-inactive",
      monthly: "monthly",
      oneTime: "one_time",
      variantSwitcherActive: "ShopAll-variantSwitcher-container--active",
      variantSwitcherInactive: "ShopAll-variantSwitcher-container--inactive"
    }
  };

  /**
   * Extract product handle from form input name
   * @param {HTMLElement} form - The product form element
   * @returns {string|null} Product handle or null if not found
   */
  function getProductHandle(form) {
    try {
      const input = form.querySelector(CONFIG.selectors.productTypeInput);
      if (!input?.name) return null;
      
      const match = input.name.match(/product-type-(.+)/);
      return match ? match[1] : null;
    } catch (error) {
      console.warn("Failed to extract product handle:", error);
      return null;
    }
  }

  /**
   * Get required DOM elements for a product form
   * @param {HTMLElement} form - The product form element
   * @param {string} productHandle - The product handle
   * @returns {Object} Object containing DOM elements
   */
  function getFormElements(form, productHandle) {
    return {
      monthlyLabel: form.querySelector(CONFIG.selectors.monthlyLabel(productHandle)),
      oneTimeLabel: form.querySelector(CONFIG.selectors.oneTimeLabel(productHandle)),
      sellingPlanInput: form.querySelector(CONFIG.selectors.sellingPlanInput(productHandle))
    };
  }

  /**
   * Toggle active/inactive states between monthly and one-time options
   * @param {HTMLElement} activeLabel - Label to make active
   * @param {HTMLElement} inactiveLabel - Label to make inactive
   * @param {HTMLElement} sellingPlanInput - Selling plan input element
   * @param {string} defaultSellingPlanId - Default selling plan ID
   */
  function toggleActiveState(activeLabel, inactiveLabel, sellingPlanInput, defaultSellingPlanId) {
    if (!activeLabel || !inactiveLabel) {
      console.warn("Missing label elements for toggle operation");
      return;
    }

    const activeCta = activeLabel.querySelector(CONFIG.selectors.shopallCta);
    const inactiveCta = inactiveLabel.querySelector(CONFIG.selectors.shopallCta);

    if (!activeCta || !inactiveCta) {
      console.warn("Missing CTA elements for toggle operation");
      return;
    }

    // Update active label
    activeCta.classList.add(CONFIG.classes.active);
    activeCta.classList.remove(CONFIG.classes.inactive);

    // Update inactive label
    inactiveCta.classList.add(CONFIG.classes.inactive);
    inactiveCta.classList.remove(CONFIG.classes.active);

    const isMonthly = activeLabel.classList.contains(CONFIG.classes.monthly);

    // Update selling plan based on selection
    if (sellingPlanInput) {
      sellingPlanInput.value = isMonthly ? (defaultSellingPlanId || "") : "";
    }

    setPurchaseType(activeLabel, isMonthly);
  }

  /**
   * @param {HTMLElement} element 
   * @param {boolean} isMonthly
   */
  function setPurchaseType(element, isMonthly) {
    const container = element.closest(CONFIG.selectors.productContainer);
    if (container) {
      container.dataset.purchaseType = isMonthly
        ? CONFIG.classes.monthly
        : CONFIG.classes.oneTime;
    }
  }

  /**
   * Toggle active variant state
   * @param {HTMLElement} selectedContainer - The container clicked
   * @param {HTMLElement[]} allContainers - All sibling containers
   * @param {HTMLElement} sellingPlanInput - Selling plan input for this product
   * @param {boolean} isMonthly - Whether monthly is currently active
   */
  function toggleVariant(selectedContainer, allContainers, sellingPlanInput, isMonthly) {
    if (!selectedContainer) return;

    // Only run toggle logic if more than one container exists
    if (allContainers.length > 1) {
      // Remove active class from all containers
      allContainers.forEach(container => {
        container.classList.remove(CONFIG.classes.variantSwitcherActive);
        container.classList.add(CONFIG.classes.variantSwitcherInactive);
      });

      // Add active to the selected one
      selectedContainer.classList.remove(CONFIG.classes.variantSwitcherInactive);
      selectedContainer.classList.add(CONFIG.classes.variantSwitcherActive);
    }

    const variantHandle = selectedContainer.dataset.variantProductHandle;
    const variantSellingPlanId = selectedContainer.dataset.variantSellingPlanId;
    const variantVariantId = selectedContainer.dataset.variantVariantId;
    const variantProductId = selectedContainer.dataset.variantProductId;
    const variantType = selectedContainer.dataset.variantType;
    const variantServings = selectedContainer.dataset.variantServings;

    // Update the selling plan input value
    if (sellingPlanInput) {
      sellingPlanInput.id = `${variantHandle}-selling_plan`;
      sellingPlanInput.value = isMonthly ? (variantSellingPlanId || "") : "";
    }

    const form = selectedContainer.closest("form");
    if (form) {
      form.id = `product-form-${variantHandle}`;

      // Update hidden input inside the form
      const hiddenInput = form.querySelector('input[type="hidden"]');
      if (hiddenInput && variantVariantId) {
        hiddenInput.value = variantVariantId;
        hiddenInput.dataset.productId = variantProductId || hiddenInput.dataset.productId || "";
      }
    }

    // Switch product images
    const productContainer = selectedContainer.closest(".shopall-product-container");
    if (!productContainer) return;

    // Only target galleries inside this container
    const galleries = productContainer.querySelectorAll(".js-product-gallery.ProductGallery");
    const hasPrimary = productContainer.querySelector(".js-product-gallery.ProductGallery.primary");
    const hasAlternate = productContainer.querySelector(".js-product-gallery.ProductGallery.alternate");

    if (hasPrimary && hasAlternate) {
      galleries.forEach(gallery => {
        const isMatch =
          (variantType === "primary" && gallery.classList.contains("primary")) ||
          (variantType === "alternate" && gallery.classList.contains("alternate"));

        if (isMatch) {
          gallery.classList.add("active-image");
          gallery.classList.remove("hidden-image");
        } else {
          gallery.classList.remove("active-image");
          gallery.classList.add("hidden-image");
        }
      });
    } else {
      // Optional: ensure that a single gallery stays visible
      galleries.forEach(gallery => {
        gallery.classList.add("active-image");
        gallery.classList.remove("hidden-image");
      });
    }

    const servings = productContainer.querySelector(".shopall-product-size");
    // Update servings amount
    if (variantServings && servings){
      servings.textContent = variantServings + " Servings"
    }
  }

  /**
   * Initialize a single product form
   * @param {HTMLElement} form - The product form element
   */
  function initializeProductForm(form) {
    const productHandle = getProductHandle(form);
    if (!productHandle) {
      console.warn("Could not extract product handle from form");
      return;
    }

    const elements = getFormElements(form, productHandle);
    const { monthlyLabel, oneTimeLabel, sellingPlanInput } = elements;

    // Skip if required elements are missing
    if (!monthlyLabel && !oneTimeLabel) {
      console.warn(`No subscription options found for product: ${productHandle}`);
    }

    const defaultSellingPlanId = sellingPlanInput?.value;

    // Subscription toggle logic
    const createClickHandler = (activeLabel, inactiveLabel) => {
      return function(event) {
        event.preventDefault();
        toggleActiveState(activeLabel, inactiveLabel, sellingPlanInput, defaultSellingPlanId);

        // Get current subscription type
        const isMonthly = activeLabel.classList.contains(CONFIG.classes.monthly);

        // If variant selector exists, reapply toggleVariant so sellingPlanInput stays in sync
        const variantSwitcherContainers = form.querySelectorAll(CONFIG.selectors.variantSwitcherContainer);
        const activevariant = form.querySelector(`.${CONFIG.classes.variantSwitcherActive}`);
        if (activevariant && variantSwitcherContainers.length > 0) {
          toggleVariant(activevariant, Array.from(variantSwitcherContainers), sellingPlanInput, isMonthly);
        }
      };
    };

    // Add event listeners
    if (monthlyLabel) {
      monthlyLabel.addEventListener("click", createClickHandler(monthlyLabel, oneTimeLabel));
    }

    if (oneTimeLabel) {
      oneTimeLabel.addEventListener("click", createClickHandler(oneTimeLabel, monthlyLabel));
    }

    // Variant selector logic
    const variantSwitcherContainers = form.querySelectorAll(CONFIG.selectors.variantSwitcherContainer);
    if (variantSwitcherContainers.length > 0) {
      variantSwitcherContainers.forEach(container => {
        container.addEventListener("click", function() {
          // Determine if monthly is active right now
          const isMonthly = monthlyLabel?.querySelector(CONFIG.selectors.shopallCta)?.classList.contains(CONFIG.classes.active) || false;

          toggleVariant(container, Array.from(variantSwitcherContainers), sellingPlanInput, isMonthly);
        });
      });
    }
  }

  // Initialize all product forms
  const productForms = document.querySelectorAll(CONFIG.selectors.productForm);
  
  if (productForms.length === 0) {
    console.info("No product forms found on page");
    return;
  }

  productForms.forEach((form, index) => {
    try {
      initializeProductForm(form);
    } catch (error) {
      console.error(`Failed to initialize product form ${index}:`, error);
    }
  });

  console.info(`Initialized ${productForms.length} product form(s)`);
});
